// Signup-flow state (zie /signup orchestrator).
// Spec: docs/features/onboarding-flow.md (T6)
//
// Apart van OnboardingTour.js (legacy product-tour state).
//
// Persistentie (2026-05-15): de gepersisteerde velden (compagnyId, userId,
// formState, modules, etc.) worden bij elke wijziging naar localStorage
// geschreven, en op mount weer ingeladen. Daardoor overleven een page-refresh
// en de Stripe-checkout round-trip de in-progress sessie. `partner` en
// `partnerLoading` worden NIET gepersist — die komen uit `usePartner` en
// moeten freshly worden gefetched van Firestore.

import { createContext, useEffect, useRef, useState } from "react";
import { auth } from "../firebase/config";

const STORAGE_KEY = 'deccos.onboardingFlow.v1'

const loadStored = () => {
    try {
        const raw = typeof window !== 'undefined' ? window.localStorage.getItem(STORAGE_KEY) : null
        return raw ? JSON.parse(raw) : {}
    } catch {
        return {}
    }
}

export const OnboardingFlow = createContext()

export const OnboardingFlowProvider = (props) => {

    // Eenmalige load uit localStorage. Lazy initializer zorgt dat dit
    // alleen op mount draait, niet op elke re-render.
    const [initial] = useState(loadStored)

    // Current step ID (number 1-11 of string 'moduleActivation')
    const [step, setStep] = useState(initial.step ?? 1)

    // Form-input accumulator per stap (account, org, etc.)
    const [formState, setFormState] = useState(initial.formState ?? {})

    // Active modules object — { impactMeten, personas, housevisits, portfolio }
    const [modules, setModules] = useState(initial.modules ?? {})

    // Locked modules — slugs die niet wijzigbaar zijn (door partner-context)
    const [lockedModules, setLockedModules] = useState(initial.lockedModules ?? [])

    // Partner-slug uit deeplink — gezet door orchestrator op basis van
    // parseDeeplink. Gebruikt door Step 4/5/6 voor pricing + Stripe-checkout.
    const [partnerSlug, setPartnerSlug] = useState(initial.partnerSlug ?? null)

    // Resolved Partner-record (uit `usePartner` in de orchestrator). Eén
    // listener voor de hele flow — stappen lezen partner via context i.p.v.
    // elk een eigen Firestore-subscription. Niet gepersist: orchestrator
    // herstelt dit door usePartner(partnerSlug) opnieuw te draaien.
    const [partner, setPartner] = useState(null)
    const [partnerLoading, setPartnerLoading] = useState(false)

    // ID van in-flight TOC-draft (voor resume-on-return bij AI-step)
    const [tocDraftId, setTocDraftId] = useState(initial.tocDraftId ?? null)

    // Foundational identifiers, gezet aan het einde van Step 1 (T11):
    //   - compagnyId: het verse uuid voor de nieuwe org's CompagnyMeta
    //   - userId: Firebase auth uid van de zojuist aangemaakte gebruiker
    // Subsequente stappen lezen deze om CompagnyMeta-updates te doen.
    const [compagnyId, setCompagnyId] = useState(initial.compagnyId ?? null)
    const [userId, setUserId] = useState(initial.userId ?? null)

    // Geselecteerde sector-slugs uit Step 3 (multi-select). Gevuld door:
    //   - sector-netwerk-deeplink: pre-checked uit netwerk-tenant
    //     `SubscribedMSISectors`
    //   - solo-org keuze in Step 3
    //   - leeg (skip): AI in Step 9 doet sector-suggestie
    const [selectedSectors, setSelectedSectors] = useState(initial.selectedSectors ?? [])

    // Write-through: bij elke wijziging van een gepersisteerd veld de hele
    // snapshot opnieuw schrijven. Goedkoop genoeg voor deze schaal (kleine
    // object, lage update-frequency) en simpeler dan partial updates.
    useEffect(() => {
        try {
            const snapshot = {
                step,
                formState,
                modules,
                lockedModules,
                partnerSlug,
                tocDraftId,
                compagnyId,
                userId,
                selectedSectors,
            }
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot))
        } catch {
            // localStorage onbeschikbaar (private mode, quota); negeren.
        }
    }, [step, formState, modules, lockedModules, partnerSlug, tocDraftId, compagnyId, userId, selectedSectors])

    // Reset alle onboarding-state én verwijder de gepersisteerde snapshot.
    // Aanroepen wanneer een onboarding "klaar" is (Step 7's "Naar mijn
    // omgeving") of expliciet opnieuw beginnen.
    const clearOnboardingState = () => {
        try { window.localStorage.removeItem(STORAGE_KEY) } catch {}
        setStep(1)
        setFormState({})
        setModules({})
        setLockedModules([])
        setPartnerSlug(null)
        setPartner(null)
        setPartnerLoading(false)
        setTocDraftId(null)
        setCompagnyId(null)
        setUserId(null)
        setSelectedSectors([])
    }

    // Wis de gepersisteerde snapshot bij uitloggen. `formState` bevat
    // email/naam/org-info (PII) — die hoort niet rond te slingeren op een
    // gedeeld device als een gebruiker uitlogt zonder de flow af te maken.
    // We detecteren de signed-in → signed-out transitie (eerste null bij
    // app-start telt niet als logout).
    const prevUidRef = useRef(null)
    useEffect(() => {
        return auth.onAuthStateChanged((user) => {
            if (prevUidRef.current && !user) {
                clearOnboardingState()
            }
            prevUidRef.current = user?.uid || null
        })
    }, [])

    return(
        <OnboardingFlow.Provider value={{
            step, setStep,
            formState, setFormState,
            modules, setModules,
            lockedModules, setLockedModules,
            partnerSlug, setPartnerSlug,
            partner, setPartner,
            partnerLoading, setPartnerLoading,
            tocDraftId, setTocDraftId,
            compagnyId, setCompagnyId,
            userId, setUserId,
            selectedSectors, setSelectedSectors,
            clearOnboardingState,
        }}>
            {props.children}
        </OnboardingFlow.Provider>
    )
}
